import { CheckCircle2, Info, Mail, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { SettingsSection } from "@/modules/dashboard/settings/components/SettingsSection";
import { DocumentsReadOnlyList } from "@/modules/verification/components/DocumentsReadOnlyList";
import { VerificationService } from "@/modules/verification/services/verificationService";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { ClientsService } from "../../services/clientsService";
import type { ClientByIdResponse } from "../../types/clients-api.types";
import { displayClientName } from "../../utils/clientsTableHelpers";
import {
  getIssuedKycLink,
  type IssuedKycLinkEntry,
  rememberIssuedKycLink,
} from "../../utils/issuedKycLinkCache";
import { KycStatusPill } from "../KycStatusPill";
import { KycLinkReveal } from "./KycLinkReveal";

export interface KycVerificationSectionProps {
  /** The verifiable entity in client-detail shape — a client, or an organization mapped into that shape. */
  client: ClientByIdResponse;
  /** Whether the current user may send the verification email (permission-gated by the caller). */
  canSendEmail?: boolean;
  /**
   * Anchor id for the section card. Override when a second instance can render on the same page (the
   * organization detail page shows the organization's KYC while a client drawer may be open over it).
   */
  sectionId?: string;
}

/**
 * KYC section of the detail view (mock SectionCard pattern via `SettingsSection`). Three states: (a) VERIFIED
 * — completion date plus the verification report (signed S3 download); (b) active link — secret-reveal block
 * with copy, optional "send by email" and regenerate; (c) no link — "Generate verification" CTA. Links are
 * issued through the identity verification engine (`POST
 * /internal/identities/verification/:identityId/kyc-link`); the legacy clients KYC endpoints are deprecated
 * and reject with 400 on qa.
 */
export function KycVerificationSection({
  client,
  canSendEmail = false,
  sectionId = "client-kyc",
}: KycVerificationSectionProps) {
  const { t } = useTranslations("clients");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [issuingLink, setIssuingLink] = useState(false);
  const [issuedLink, setIssuedLink] = useState<IssuedKycLinkEntry | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Drop per-client transient state when the displayed client changes (subclient switch, org ↔ subclient).
  useEffect(() => {
    setIssuedLink(null);
    setActionError(null);
  }, [client.id]);

  const isVerified = client.kyc_status === "VERIFIED";
  // Engine link resolution: section state (just issued here) → backend active link (stubbed null on qa) →
  // session cache (issued during the create flow or an earlier visit). The cache avoids re-issuing just to
  // display it — a new issue expires the previous link on the backend.
  const activeLink: IssuedKycLinkEntry | null =
    issuedLink ??
    (client.activeKycLinkVerificationUrl?.trim()
      ? { url: client.activeKycLinkVerificationUrl.trim(), expiresAt: client.activeKycLinkExpiresAt }
      : isVerified
        ? null
        : (client.identity_id && getIssuedKycLink(client.identity_id)) || null);
  const link = activeLink?.url ?? null;
  const linkExpiresAt = activeLink?.expiresAt ?? null;
  const hasValidLink = Boolean(link && /^https?:\/\//i.test(link));
  const canIssueLink = Boolean(client.identity_id) && !isVerified;
  const showSendEmail = canSendEmail && !isVerified && hasValidLink && Boolean(client.email?.trim());

  const handleIssueLink = useCallback(async () => {
    if (!client.identity_id) return;
    setIssuingLink(true);
    setActionError(null);
    try {
      const { data } = await VerificationService.issueLink(client.identity_id);
      const entry: IssuedKycLinkEntry = { url: data.verification_url, expiresAt: data.expires_at ?? null };
      rememberIssuedKycLink(client.identity_id, entry);
      setIssuedLink(entry);
    } catch (err) {
      setActionError(HttpApiService.getErrorMessage(err));
    } finally {
      setIssuingLink(false);
    }
  }, [client.identity_id]);

  const handleSendEmail = useCallback(async () => {
    if (!link || !client.email?.trim()) return;
    setSendingEmail(true);
    setActionError(null);
    try {
      // The mail template expects a duration in hours; the engine gives us the expiry timestamp.
      const expiresAtMs = linkExpiresAt ? new Date(linkExpiresAt).getTime() : Number.NaN;
      const expiresInHours = Number.isNaN(expiresAtMs)
        ? ""
        : String(Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 3_600_000)));
      await ClientsService.sendKycVerificationMail({
        type: "OPERATIONS_KYC_VERIFICATION",
        to: client.email.trim(),
        payload: {
          name: displayClientName(client),
          verificationUrl: link,
          expiresInHours,
        },
      });
      colocaToast.success(t("view_toast_send_email_success"));
    } catch (err) {
      setActionError(HttpApiService.getErrorMessage(err));
      colocaToast.error(t("view_toast_send_email_error"));
    } finally {
      setSendingEmail(false);
    }
  }, [client, link, linkExpiresAt, t]);

  const verifiedDate = client.kyc_verified_at
    ? formatDateTime(client.kyc_verified_at, { includeTime: false })
    : "—";

  return (
    <SettingsSection
      id={sectionId}
      eyebrow={t("kyc_section_eyebrow")}
      title={t("kyc_section_title")}
      sub={t("kyc_section_sub")}
      headerRight={<KycStatusPill status={client.kyc_status} />}
    >
      {isVerified ? (
        <div className="space-y-3">
          <ColocaAlert
            variant="success"
            icon={CheckCircle2}
            text={t("kyc_verified_on", { date: verifiedDate })}
          />
          {client.identity_id && (
            <DocumentsReadOnlyList
              identityId={client.identity_id}
              hasDocument={Boolean(client.kyc_verified_at || (client.kyc_documents?.length ?? 0) > 0)}
            />
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {client.kyc_status === "REJECTED" && client.kyc_rejection_reason && (
            <ColocaAlert
              variant="destructive"
              icon={XCircle}
              text={t("kyc_rejection_reason", { reason: client.kyc_rejection_reason })}
            />
          )}
          {actionError && <ColocaAlert variant="destructive" text={actionError} />}
          {hasValidLink && link ? (
            <>
              <KycLinkReveal link={link} expiresAt={linkExpiresAt} />
              <div className="flex flex-wrap items-center gap-2">
                {showSendEmail && (
                  <ColocaButton
                    type="button"
                    variant="primary"
                    size="sm"
                    icon={Mail}
                    loading={sendingEmail}
                    disabled={sendingEmail || issuingLink}
                    onClick={() => void handleSendEmail()}
                  >
                    {t("view_btn_send_email")}
                  </ColocaButton>
                )}
                {canIssueLink && (
                  <ColocaButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    icon={RefreshCw}
                    loading={issuingLink}
                    disabled={issuingLink || sendingEmail}
                    onClick={() => void handleIssueLink()}
                  >
                    {t("kyc_btn_regenerate")}
                  </ColocaButton>
                )}
              </div>
            </>
          ) : canIssueLink ? (
            <ColocaButton
              type="button"
              variant="primary"
              size="sm"
              icon={ShieldCheck}
              loading={issuingLink}
              disabled={issuingLink}
              onClick={() => void handleIssueLink()}
            >
              {t("view_btn_generate_link")}
            </ColocaButton>
          ) : (
            <ColocaAlert variant="secondary" icon={Info} text={t("kyc_no_link")} />
          )}
        </div>
      )}
    </SettingsSection>
  );
}
